import { supabase } from './supabase.js';
import { SETTING_DEFAULTS, DEFAULT_MEMBERS } from './content-defaults.js';

// --- Nastavení -------------------------------------------------------------
let settingsCache = null;

export async function getSettings() {
    if (settingsCache) return settingsCache;
    const map = { ...SETTING_DEFAULTS };
    const { data, error } = await supabase.from('web_settings').select('key, value');
    if (error) {
        console.error('settings:', error.message);
    } else {
        // Neprázdná hodnota z DB přebije výchozí obsah.
        for (const row of data || []) {
            if (row.value !== '' && row.value != null) map[row.key] = row.value;
        }
    }
    settingsCache = map;
    return map;
}

export async function getBandName() {
    const s = await getSettings();
    return (s.band_name || 'Amati Band').toString().trim() || 'Amati Band';
}

// --- Koncerty --------------------------------------------------------------
let eventsCache = null;
export async function getEvents() {
    if (eventsCache) return eventsCache;
    const { data, error } = await supabase
        .from('web_concerts')
        .select('*')
        .order('event_date', { ascending: true });
    if (error) { console.error('concerts:', error.message); return { upcoming: [], past: [] }; }

    const today = new Date().toISOString().slice(0, 10);
    const upcoming = [];
    const past = [];
    for (const e of data || []) {
        const end = e.event_date_end || e.event_date;
        if (end >= today) upcoming.push(e);
        else past.push(e);
    }
    past.reverse();
    eventsCache = { upcoming, past };
    return eventsCache;
}

export async function getNextEvent() {
    const { upcoming } = await getEvents();
    return upcoming[0] || null;
}

// --- Aktuality -------------------------------------------------------------
export async function getNews(limit) {
    let q = supabase
        .from('web_news')
        .select('*')
        .eq('published', true)
        .order('published_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });
    if (limit) q = q.limit(limit);
    const { data, error } = await q;
    if (error) { console.error('news:', error.message); return []; }
    return data || [];
}

export async function getNewsItem(id) {
    const { data, error } = await supabase
        .from('web_news').select('*').eq('id', id).eq('published', true).maybeSingle();
    if (error) { console.error('news item:', error.message); return null; }
    return data;
}

// --- Členové ---------------------------------------------------------------
export async function getMembers() {
    const { data, error } = await supabase
        .from('web_members').select('*')
        .order('position', { ascending: true })
        .order('name', { ascending: true });
    if (error) { console.error('members:', error.message); return DEFAULT_MEMBERS; }
    // Dokud nejsou členové v DB, použij výchozí obsazení.
    return (data && data.length) ? data : DEFAULT_MEMBERS;
}

// --- Hudba a videa (soubory v bucketu web-media/media/) ---------------------
const AUDIO_EXT = /\.(mp3|wav|ogg|m4a|aac|flac)$/i;
const VIDEO_EXT = /\.(mp4|webm|ogv|mov|m4v)$/i;

let mediaCache = null;
export async function getMediaFiles() {
    if (mediaCache) return mediaCache;
    const [{ data, error }, settings] = await Promise.all([
        supabase.storage.from('web-media')
            .list('media', { limit: 500, sortBy: { column: 'name', order: 'asc' } }),
        getSettings()
    ]);
    if (error) { console.error('media:', error.message); return []; }
    const namesRaw = settings.media_names;
    const names = (namesRaw && typeof namesRaw === 'object' && !Array.isArray(namesRaw)) ? namesRaw : {};
    const items = (data || [])
        .filter((e) => e.id !== null && (AUDIO_EXT.test(e.name) || VIDEO_EXT.test(e.name)))
        .map((e) => ({
            name: e.name,
            path: `media/${e.name}`,
            label: names[e.name] || e.name.replace(/\.[^.]+$/, ''),
            kind: VIDEO_EXT.test(e.name) ? 'video' : 'audio'
        }));
    const order = Array.isArray(settings.media_order) ? settings.media_order : [];
    const idx = new Map(order.map((n, i) => [n, i]));
    items.sort((a, b) =>
        (idx.has(a.name) ? idx.get(a.name) : 1e9) - (idx.has(b.name) ? idx.get(b.name) : 1e9));
    mediaCache = items;
    return items;
}

// --- Fotogalerie (složky v bucketu web-media/gallery/) ----------------------
const GALLERY_IMG = /\.(jpe?g|png|webp|gif|avif)$/i;

// Alba = podsložky gallery/. key = složka, name = zobrazovaný název.
let albumsCache = null;
const albumPhotosCache = new Map();

export async function getGalleryAlbums() {
    if (albumsCache) return albumsCache;
    const [{ data, error }, settings] = await Promise.all([
        supabase.storage.from('web-media')
            .list('gallery', { limit: 200, sortBy: { column: 'name', order: 'asc' } }),
        getSettings()
    ]);
    if (error) { console.error('albums:', error.message); return []; }
    const folders = (data || []).filter((e) => e.id === null && !e.name.startsWith('.'));

    const namesRaw = settings.gallery_names;
    const names = (namesRaw && typeof namesRaw === 'object' && !Array.isArray(namesRaw)) ? namesRaw : {};

    // Načtení fotek pro všechna alba paralelně — místo sekvenčně, kde každé čekalo na předchozí.
    const photoLists = await Promise.all(folders.map((f) => getAlbumPhotos(f.name)));
    const albums = folders.map((f, i) => ({
        key: f.name,
        name: names[f.name] || f.name,
        count: photoLists[i].length,
        cover: photoLists[i][0] || null
    }));

    // Pořadí podle web_settings.gallery_order; alba mimo seznam jdou nahoru.
    const order = Array.isArray(settings.gallery_order) ? settings.gallery_order : [];
    const idx = new Map(order.map((n, i) => [n, i]));
    albums.sort((a, b) =>
        (idx.has(a.key) ? idx.get(a.key) : -1) - (idx.has(b.key) ? idx.get(b.key) : -1));

    albumsCache = albums;
    return albums;
}

// Cesty k fotkám v jednom albu, seřazené dle uloženého pořadí (první = náhled).
export async function getAlbumPhotos(album) {
    if (albumPhotosCache.has(album)) return albumPhotosCache.get(album);
    const [{ data, error }, settings] = await Promise.all([
        supabase.storage.from('web-media')
            .list('gallery/' + album, { limit: 500, sortBy: { column: 'name', order: 'asc' } }),
        getSettings()
    ]);
    if (error) { console.error('album:', error.message); return []; }
    const files = (data || [])
        .filter((e) => e.id !== null && GALLERY_IMG.test(e.name))
        .map((e) => e.name);
    const orderMapRaw = settings.gallery_photo_order;
    const orderMap = (orderMapRaw && typeof orderMapRaw === 'object' && !Array.isArray(orderMapRaw))
        ? orderMapRaw : {};
    const arr = orderMap[album] || [];
    const idx = new Map(arr.map((n, i) => [n, i]));
    files.sort((a, b) => (idx.has(a) ? idx.get(a) : 1e9) - (idx.has(b) ? idx.get(b) : 1e9));
    const paths = files.map((name) => `gallery/${album}/${name}`);
    albumPhotosCache.set(album, paths);
    return paths;
}

// --- Anketa: tipy na písně --------------------------------------------------
export async function submitSongRequest(song) {
    const { error } = await supabase
        .from('web_song_requests').insert({ song });
    if (error) throw error;
}

